import { reactive, ref } from 'vue'
import { invoke } from '~/lib/tauriInvoke'
import { runAction } from '~/stores/storeErrorHandling'
import {
  AgentTaskAction,
  type AgentTask,
  type AgentTaskRuns,
  type AgentTaskSettings,
  type AgentTasks,
  type CreateAgentTaskRequest,
  type UpdateAgentTaskRequest,
} from '~/types/agent-tasks'

export {
  AgentTaskAction,
  AgentTaskKind,
  AgentTaskRunStatus,
  AgentTaskStatus,
} from '~/types/agent-tasks'
export type {
  AgentTask,
  AgentTaskRun,
  AgentTaskRuns,
  AgentTaskSettings,
  CreateAgentTaskRequest,
  UpdateAgentTaskRequest,
} from '~/types/agent-tasks'

export interface AgentTasksState {
  loaded: boolean
  loading: boolean
  loadError: string | null
  /** Whether the list includes completed and cancelled tasks. */
  includeFinished: boolean
  tasks: AgentTask[]
}

// Module-level (singleton) cache keyed by bot id — same reasoning as `useAgentMcpServers`: the bot
// panel is torn down on navigation. Unlike MCP servers, the agent itself also creates tasks (from
// chat) and runs change `next_run_at`/`last_status` server-side, so the tab re-reads the list every
// time it's shown; the cache just keeps the old list on screen meanwhile.
const stateByBot = reactive<Record<string, AgentTasksState>>({})

// Limits are the same for every agent — fetched once per session.
const settings = ref<AgentTaskSettings | null>(null)
let settingsRequest: Promise<AgentTaskSettings | null> | null = null

/** Logout: the next account must not see this one's tasks (see `clearUserSession`). */
export function resetAgentTasks() {
  for (const botId of Object.keys(stateByBot)) delete stateByBot[botId]
  settings.value = null
  settingsRequest = null
}

function emptyState(): AgentTasksState {
  return { loaded: false, loading: false, loadError: null, includeFinished: false, tasks: [] }
}

export function useAgentTasks() {
  const noopSetError = () => {}

  function stateFor(botId: string): AgentTasksState {
    if (!stateByBot[botId]) stateByBot[botId] = emptyState()
    return stateByBot[botId]
  }

  async function ensureSettings(): Promise<AgentTaskSettings | null> {
    if (settings.value) return settings.value
    settingsRequest ??= runAction(
      noopSetError,
      { action: 'get_agent_task_settings', rethrow: false },
      () => invoke<AgentTaskSettings>('get_agent_task_settings'),
    ).then((result) => {
      settings.value = (result as AgentTaskSettings | undefined) ?? null
      if (!settings.value) settingsRequest = null
      return settings.value
    })
    return settingsRequest
  }

  async function load(botId: string, includeFinished?: boolean) {
    const state = stateFor(botId)
    if (includeFinished !== undefined) state.includeFinished = includeFinished
    const wantFinished = state.includeFinished
    state.loading = true
    try {
      const result = await runAction(
        (msg) => (state.loadError = msg),
        { action: 'list_agent_tasks', extra: { botId }, rethrow: false },
        () => invoke<AgentTasks>('list_agent_tasks', { botId, includeFinished: wantFinished }),
      ) as AgentTasks | undefined
      // The toggle flipped again while this was in flight — the newer request wins.
      if (result && state.includeFinished === wantFinished) {
        state.tasks = result.tasks
        state.loaded = true
        state.loadError = null
      }
    } finally {
      state.loading = false
    }
  }

  /** Puts a fresh copy of a task into the list (or drops it, if the list hides finished ones). */
  function applyTask(task: AgentTask, prepend = false) {
    const state = stateFor(task.bot_id)
    const index = state.tasks.findIndex((t) => t.id === task.id)
    if (index !== -1) state.tasks[index] = task
    else if (prepend) state.tasks.unshift(task)
    else state.tasks.push(task)
  }

  async function createTask(request: CreateAgentTaskRequest): Promise<AgentTask> {
    const task = await runAction(
      noopSetError,
      { action: 'create_agent_task', extra: { botId: request.bot_id } },
      () => invoke<AgentTask>('create_agent_task', { payload: request }),
    ) as AgentTask
    applyTask(task, true)
    return task
  }

  async function updateTask(request: UpdateAgentTaskRequest): Promise<AgentTask> {
    const task = await runAction(
      noopSetError,
      { action: 'update_agent_task', extra: { botId: request.bot_id, taskId: request.task_id } },
      () => invoke<AgentTask>('update_agent_task', { payload: request }),
    ) as AgentTask
    applyTask(task)
    return task
  }

  async function setTaskState(botId: string, taskId: string, action: AgentTaskAction): Promise<AgentTask> {
    const task = await runAction(
      noopSetError,
      { action: 'set_agent_task_state', extra: { botId, taskId, taskAction: action } },
      () => invoke<AgentTask>('set_agent_task_state', { botId, taskId, action }),
    ) as AgentTask
    const state = stateFor(botId)
    if (action === AgentTaskAction.CANCEL && !state.includeFinished) {
      state.tasks = state.tasks.filter((t) => t.id !== taskId)
    } else {
      applyTask(task)
    }
    return task
  }

  async function deleteTask(botId: string, taskId: string): Promise<void> {
    await runAction(
      noopSetError,
      { action: 'delete_agent_task', extra: { botId, taskId } },
      () => invoke<void>('delete_agent_task', { botId, taskId }),
    )
    const state = stateFor(botId)
    state.tasks = state.tasks.filter((t) => t.id !== taskId)
  }

  /** One page of run history, newest first; `before` = the previous page's `next_before`. Throws. */
  async function fetchRuns(botId: string, taskId: string, before?: string | null, limit = 20): Promise<AgentTaskRuns> {
    return await runAction(
      noopSetError,
      { action: 'get_agent_task_runs', extra: { botId, taskId } },
      () => invoke<AgentTaskRuns>('get_agent_task_runs', { botId, taskId, limit, before: before ?? null }),
    ) as AgentTaskRuns
  }

  return {
    settings,
    stateFor,
    ensureSettings,
    load,
    createTask,
    updateTask,
    setTaskState,
    deleteTask,
    fetchRuns,
  }
}
